import { Menu, Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import { BellAlertIcon, BellIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import React, { Fragment } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error' | 'alert';
  is_read: boolean;
  link?: string;
  created_at: string;
}

const TYPE_STYLES: Record<Notification['type'], { icon: React.ElementType; color: string }> = {
  info: { icon: InformationCircleIcon, color: 'text-blue-500' },
  warning: { icon: ExclamationTriangleIcon, color: 'text-amber-500' },
  success: { icon: CheckCircleIcon, color: 'text-green-500' },
  error: { icon: XCircleIcon, color: 'text-red-500' },
  alert: { icon: ShieldExclamationIcon, color: 'text-orange-500' },
};

const NotificationDropdown: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: rawNotifications, isLoading } = useQuery<any>({
    queryKey: ['notifications'],
    queryFn: async () => {
      return await apiService.get('/notifications/', { params: { limit: 20 } });
    },
    refetchInterval: 30000,
  });

  // Safely extract array from API response (handles wrapped {data:[...]} and direct [...])
  const notifications: Notification[] = Array.isArray(rawNotifications)
    ? rawNotifications
    : Array.isArray(rawNotifications?.data)
      ? rawNotifications.data
      : [];

  const { data: unreadCount } = useQuery<number>({
    queryKey: ['unreadNotificationsCount'],
    queryFn: async () => {
      return await apiService.get<number>('/notifications/unread-count');
    },
    refetchInterval: 30000,
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiService.put(`/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationsCount'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await apiService.put(`/notifications/read-all`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationsCount'] });
      toast.success('All notifications marked as read');
    },
  });

  const scanAlertsMutation = useMutation({
    mutationFn: async () => {
      return await apiService.post<number>('/notifications/scan-alerts');
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationsCount'] });
      if (count && count > 0) {
        toast.success(`Found ${count} new alert${count > 1 ? 's' : ''}`);
      } else {
        toast('No new alerts found', { icon: '✅' });
      }
    },
    onError: () => {
      toast.error('Failed to scan for alerts');
    },
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const hasUnread = unreadCount != null && unreadCount > 0;

  return (
    <Menu as="div" className="relative">
      <div>
        <Menu.Button className="relative rounded-full bg-white dark:bg-gray-800 p-1.5 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors">
          <span className="sr-only">View notifications</span>
          {hasUnread ? (
            <BellAlertIcon
              className="h-6 w-6 text-blue-500 animate-[wiggle_0.5s_ease-in-out]"
              aria-hidden="true"
            />
          ) : (
            <BellIcon className="h-6 w-6" aria-hidden="true" />
          )}
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-800">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95 -translate-y-1"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 scale-100 translate-y-0"
        leaveTo="transform opacity-0 scale-95 -translate-y-1"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-96 origin-top-right rounded-lg bg-white dark:bg-gray-800 shadow-xl ring-1 ring-black/5 dark:ring-gray-700 focus:outline-none">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
              {hasUnread && (
                <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scanAlertsMutation.mutate();
                }}
                disabled={scanAlertsMutation.isPending}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50 transition-colors"
                title="Scan for new alerts"
              >
                {scanAlertsMutation.isPending ? 'Scanning…' : 'Scan'}
              </button>
              {hasUnread && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      markAllReadMutation.mutate();
                    }}
                    disabled={markAllReadMutation.isPending}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 disabled:opacity-50 transition-colors"
                  >
                    Mark all read
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-[28rem] overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading…</p>
              </div>
            ) : notifications && notifications.length > 0 ? (
              notifications.map((notification) => {
                const style = TYPE_STYLES[notification.type] ?? TYPE_STYLES.info;
                const Icon = style.icon;
                return (
                  <Menu.Item key={notification.id}>
                    {({ active }) => (
                      <div
                        onClick={() => handleNotificationClick(notification)}
                        className={clsx(
                          'flex gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 dark:border-gray-700/50 last:border-0 transition-colors',
                          active && 'bg-gray-50 dark:bg-gray-700/50',
                          !notification.is_read && 'bg-blue-50/60 dark:bg-blue-900/20'
                        )}
                      >
                        <Icon className={clsx('h-5 w-5 mt-0.5 flex-shrink-0', style.color)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start gap-2">
                            <p
                              className={clsx(
                                'text-sm truncate',
                                !notification.is_read
                                  ? 'font-semibold text-gray-900 dark:text-white'
                                  : 'font-medium text-gray-600 dark:text-gray-300'
                              )}
                            >
                              {notification.title}
                            </p>
                            <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                              {formatDistanceToNow(new Date(notification.created_at), {
                                addSuffix: true,
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          {notification.link && (
                            <p className="text-xs text-blue-500 dark:text-blue-400 mt-1 hover:underline">
                              View details →
                            </p>
                          )}
                        </div>
                        {!notification.is_read && (
                          <span className="h-2 w-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                        )}
                      </div>
                    )}
                  </Menu.Item>
                );
              })
            ) : (
              <div className="px-4 py-10 text-center">
                <BellIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    scanAlertsMutation.mutate();
                  }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
                >
                  Scan for alerts
                </button>
              </div>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default NotificationDropdown;
